import { motion } from "framer-motion";

import { cn } from "@/lib/utils";

interface M1GaugeProps {
  /** Valore corrente da mostrare (giorni). null = nessun dato. */
  valore: number | null;
  /** Target operativo (es. 14). Disegnato come tacca evidenziata. */
  target: number;
  /** Soglia tra zona verde e gialla. */
  sogliaGiallo: number;
  /** Soglia tra zona gialla e rossa. */
  sogliaRosso: number;
  /** Massimo della scala (clamp visivo). Default 2× sogliaRosso. */
  max?: number;
  /** Etichetta sotto il numero. */
  unitaLabel?: string;
}

/**
 * Gauge semicircolare verde/giallo/rosso con lancetta animata.
 *
 * Mappatura giorni → angolo: lineare da -90° (0g) a +90° (max). Le 3 zone
 * coloratte coprono [0..sogliaGiallo], (sogliaGiallo..sogliaRosso],
 * (sogliaRosso..max]. Il `target` è disegnato come tacca scura per riferimento.
 */
export function M1Gauge({
  valore,
  target,
  sogliaGiallo,
  sogliaRosso,
  max,
  unitaLabel = "giorni mediani",
}: M1GaugeProps) {
  const scalaMax = max ?? sogliaRosso * 2;
  const cx = 110;
  const cy = 110;
  const r = 90;
  const archStrokeWidth = 18;

  const valoreClamped = valore == null ? null : Math.max(0, Math.min(scalaMax, valore));
  const angoloLancetta = valoreClamped == null ? null : giorniToAngolo(valoreClamped, scalaMax);
  const tone =
    valore == null
      ? "neutro"
      : valore <= sogliaGiallo
        ? "verde"
        : valore <= sogliaRosso
          ? "giallo"
          : "rosso";

  return (
    <div className="space-y-3">
      <svg
        viewBox="-15 -14 250 162"
        className="w-full max-w-[260px]"
        role="img"
        aria-label={
          valore == null
            ? "Lead time: nessun dato"
            : `Lead time mediano ${valore.toFixed(0)} giorni, target ${target}`
        }
      >
        {/* Arco verde 0..sogliaGiallo */}
        <path
          d={archPath(cx, cy, r, 0, sogliaGiallo, scalaMax)}
          fill="none"
          stroke="hsl(142 70% 45%)"
          strokeWidth={archStrokeWidth}
          strokeLinecap="butt"
        />
        {/* Arco giallo sogliaGiallo..sogliaRosso */}
        <path
          d={archPath(cx, cy, r, sogliaGiallo, sogliaRosso, scalaMax)}
          fill="none"
          stroke="hsl(43 92% 55%)"
          strokeWidth={archStrokeWidth}
          strokeLinecap="butt"
        />
        {/* Arco rosso sogliaRosso..scalaMax */}
        <path
          d={archPath(cx, cy, r, sogliaRosso, scalaMax, scalaMax)}
          fill="none"
          stroke="hsl(0 75% 55%)"
          strokeWidth={archStrokeWidth}
          strokeLinecap="butt"
        />

        {/* Etichette numeriche soglie sull'arco */}
        <SogliaLabel cx={cx} cy={cy} r={r} value={0} scalaMax={scalaMax} dy={20} />
        {/* Se il target coincide con una soglia — e da agosto 2026 è così: il target
            contrattuale è 21 come il confine verde/giallo — l'etichetta della soglia
            si sovrappone a quella del target. Ne si disegna una sola, e la dice la
            tacca del target. */}
        {target !== sogliaGiallo && (
          <SogliaLabel cx={cx} cy={cy} r={r + 22} value={sogliaGiallo} scalaMax={scalaMax} />
        )}
        {target !== sogliaRosso && (
          <SogliaLabel cx={cx} cy={cy} r={r + 22} value={sogliaRosso} scalaMax={scalaMax} />
        )}
        <SogliaLabel cx={cx} cy={cy} r={r} value={scalaMax} scalaMax={scalaMax} dy={20} />

        {/* Tacca target (più piccola, scura) */}
        <TargetTick cx={cx} cy={cy} r={r} target={target} scalaMax={scalaMax} />

        {/* Lancetta — animiamo direttamente le coordinate dell'estremità in viewBox,
            così la rotazione non dipende da transform-origin (in SVG con CSS-units
            ambiguo). `giorniToAngolo` è già nel sistema di `polar()`, niente shift. */}
        {angoloLancetta != null &&
          (() => {
            const lunghezza = r - 12;
            const endStatico = polar(cx, cy, lunghezza, -90); // posizione iniziale = ovest
            const endTarget = polar(cx, cy, lunghezza, angoloLancetta);
            return (
              <>
                <motion.line
                  x1={cx}
                  y1={cy}
                  initial={{ x2: endStatico.x, y2: endStatico.y }}
                  animate={{ x2: endTarget.x, y2: endTarget.y }}
                  transition={{ type: "spring", stiffness: 60, damping: 14 }}
                  stroke="var(--foreground)"
                  strokeWidth={3}
                  strokeLinecap="round"
                />
                <circle cx={cx} cy={cy} r={6} fill="var(--foreground)" />
              </>
            );
          })()}
      </svg>

      {/* Numero + unità */}
      <div className="space-y-1">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span
            className={cn(
              "text-4xl font-semibold tracking-tight tabular-nums",
              tone === "verde" && "text-success",
              tone === "giallo" && "text-warning",
              tone === "rosso" && "text-destructive",
              tone === "neutro" && "text-muted-foreground"
            )}
          >
            {valore == null ? "—" : `${valore.toFixed(0)}g`}
          </span>
          {valore != null && (
            <span className="text-xs text-muted-foreground">
              vs target <span className="font-medium text-foreground">{target}g</span>{" "}
              · {valore - target >= 0 ? "+" : ""}
              {(valore - target).toFixed(0)}g
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{unitaLabel}</p>
      </div>
    </div>
  );
}

// ── Helpers SVG ──────────────────────────────────────────────────────

function giorniToAngolo(giorni: number, scalaMax: number): number {
  // -90° = sinistra (0g), +90° = destra (scalaMax)
  return -90 + (giorni / scalaMax) * 180;
}

function polar(cx: number, cy: number, r: number, angoloDeg: number) {
  const rad = ((angoloDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function archPath(
  cx: number,
  cy: number,
  r: number,
  daGiorni: number,
  aGiorni: number,
  scalaMax: number
): string {
  // `giorniToAngolo` ritorna già un angolo nel sistema atteso da `polar()`
  // (-90=ovest, 0=top, +90=est), nessun ulteriore shift necessario.
  const angDa = giorniToAngolo(daGiorni, scalaMax);
  const angA = giorniToAngolo(aGiorni, scalaMax);
  const start = polar(cx, cy, r, angDa);
  const end = polar(cx, cy, r, angA);
  const largeArc = angA - angDa > 180 ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

interface SogliaLabelProps {
  cx: number;
  cy: number;
  r: number;
  value: number;
  scalaMax: number;
  align?: "start" | "middle" | "end";
  /** Scostamento verticale: serve agli estremi della scala, che cadono *sul*
   *  diametro e quindi si appoggerebbero addosso all'arco. */
  dy?: number;
}

function SogliaLabel({ cx, cy, r, value, scalaMax, align = "middle", dy = 0 }: SogliaLabelProps) {
  const ang = giorniToAngolo(value, scalaMax);
  const p = polar(cx, cy, r, ang);
  return (
    <text
      x={p.x}
      y={p.y + dy}
      textAnchor={align}
      dominantBaseline="central"
      className="fill-muted-foreground text-[10px] font-mono"
    >
      {value}g
    </text>
  );
}

interface TargetTickProps {
  cx: number;
  cy: number;
  r: number;
  target: number;
  scalaMax: number;
}

function TargetTick({ cx, cy, r, target, scalaMax }: TargetTickProps) {
  const ang = giorniToAngolo(target, scalaMax);
  const inner = polar(cx, cy, r - 14, ang);
  const outer = polar(cx, cy, r + 6, ang);
  const labelAt = polar(cx, cy, r + 14, ang);
  return (
    <>
      <line
        x1={inner.x}
        y1={inner.y}
        x2={outer.x}
        y2={outer.y}
        stroke="var(--foreground)"
        strokeWidth={2}
      />
      <text
        x={labelAt.x}
        y={labelAt.y - 8}
        textAnchor="middle"
        className="fill-foreground text-[9px] font-semibold uppercase tracking-wider"
      >
        target {target}g
      </text>
    </>
  );
}
